/**
 * 扫描枪输入处理工具类
 * 提供统一的扫描枪输入处理逻辑，支持不同场景下的复用
 */

/**
 * 创建一个扫描处理器
 * @param {Object} options 配置选项
 * @param {Function} options.onComplete 扫描完成回调，接收扫描结果作为参数
 * @param {Function} options.onInput 输入时回调，用于更新UI等 (可选)
 * @param {Number} options.minLength 最小有效长度，默认4 (可选)
 * @param {Number} options.maxInterval 判断为扫描的最大按键间隔(ms)，默认50 (可选)
 * @return {Object} 扫描处理器对象
 */
export function createScannerHandler(options) {
  // 合并默认选项
  const {
    onComplete,
    onInput = () => {},
    minLength = 4,
    maxInterval = 50
  } = options;

  // 扫描状态
  const state = {
    buffer: '',            // 输入缓冲区
    realBuffer: '',        // 实时缓冲区 (用于检测扫码枪)
    lastKeyTime: 0,        // 上次按键时间
    lastKey: '',           // 上次按键
    lastGap: 0             // 上次时间间隔
  };

  /**
   * 处理键盘抬起事件
   * @param {KeyboardEvent} e 键盘事件
   */
  function handleKeyUp(e) {
    // 记录当前时间
    const now = new Date().getTime();
    
    // 计算时间间隔
    let gap = 0;
    let tempGap = 0;
    
    if (state.lastKeyTime) {
      gap = now - state.lastKeyTime;
      if (gap > maxInterval) {
        tempGap = gap;
        gap = 0;
        state.realBuffer = '';
        state.lastKey = '';
      }
    }
    
    // 更新最后按键时间
    state.lastKeyTime = now;
    
    // 处理按键输入 (输入法会触发keyUp事件，key为Process，直接跳过)
    if (e.key !== 'Process' && gap < maxInterval) {
      // 单个字符按键处理
      if (e.key.trim().length === 1) {
        // 处理大小写
        if (state.lastKey === 'Shift') {
          state.realBuffer += e.key.toUpperCase();
          state.lastKey = '';
        } else {
          state.realBuffer += e.key;
        }
        
        // 记录时间间隔
        state.lastGap = tempGap;
        
        // 实时更新缓冲区内容（用于UI显示）
        state.buffer = state.realBuffer;
        onInput(state.buffer);
      } 
      // Enter键处理  
      else if (e.key.trim() === 'Enter') {
        if (state.realBuffer) {
          // 使用扫码输入的内容
          const scanText = state.realBuffer;
          
          // 判断是否符合最小长度要求
          if (scanText.length >= minLength) {
            // 更新缓冲区
            state.buffer = scanText;
            onInput(state.buffer);
            
            // 触发完成回调
            onComplete(scanText);
          }
          
          // 重置缓冲区
          state.realBuffer = '';
          state.buffer = '';
          onInput(state.buffer);
        } else if (state.buffer) {
          // 使用已有的缓冲区内容
          const scanText = state.buffer;
          
          // 判断是否符合最小长度要求
          if (scanText.length >= minLength) {
            // 触发完成回调
            onComplete(scanText);
          }
          
          // 重置缓冲区
          state.buffer = '';
          onInput(state.buffer);
        }
      } 
      // Shift键处理
      else if (e.key.trim() === 'Shift') {
        state.lastKey = e.key.trim();
      }
    } 
    // 处理中文输入法回车键
    else if (e.key === 'Process' && e.code === 'Enter' && state.buffer) {
      // 判断是否符合最小长度要求
      if (state.buffer.length >= minLength) {
        // 触发完成回调
        onComplete(state.buffer);
      }
      
      // 重置缓冲区
      state.buffer = '';
      state.realBuffer = '';
      onInput(state.buffer);
    }
  }

  /**
   * 设置缓冲区内容
   * @param {string} text 要设置的内容
   */
  function setBuffer(text) {
    state.buffer = text;
    state.realBuffer = text;
    onInput(state.buffer);
  }

  /**
   * 清空缓冲区
   */
  function clearBuffer() {
    state.buffer = '';
    state.realBuffer = '';
    onInput(state.buffer);
  }

  /**
   * 手动触发完成回调
   */
  function triggerComplete() {
    if (state.buffer && state.buffer.length >= minLength) {
      onComplete(state.buffer);
      clearBuffer();
    }
  }

  // 返回处理器对象
  return {
    handleKeyUp,
    setBuffer,
    clearBuffer,
    triggerComplete
  };
}

export default {
  createScannerHandler
}; 


// WEBPACK FOOTER //
// ./src/utils/scannerUtils.js